/**
 * Image optimization utilities using Supabase Storage transformations
 * Provides consistent image sizing and optimization across the app
 * STRICT TYPING: All image operations must be type-safe
 * 
 * BILLING OPTIMIZATION: Consolidated to 2 sizes (150px, 300px) + 2 qualities
 * to minimize unique transformations and reduce Supabase billing costs.
 * Each unique parameter combination creates a separate billable transformation.
 */

import { supabase } from './supabase.js'

/** Image size constants with strict typing - CONSOLIDATED TO REDUCE BILLING */
export const IMAGE_SIZES = {
  SMALL: 150, // Avatars, small cards, game images (was AVATAR: 48, CARD: 150, GAME: 160)
  LARGE: 300, // Profile headers, detailed views (was PROFILE: 300)
  ORIGINAL: null // No transformation
} as const

/** Image quality constants with strict typing */
export const IMAGE_QUALITY = {
  HIGH: 90, // Profile photos, detailed views
  MEDIUM: 75, // Standard cards and thumbnails
  LOW: 60 // Small avatars, background images
} as const

/** Type definitions for image utilities */
export type ImageSize = typeof IMAGE_SIZES[keyof typeof IMAGE_SIZES]
export type ImageQuality = typeof IMAGE_QUALITY[keyof typeof IMAGE_QUALITY]

export interface ImageOptimizationOptions {
  readonly width?: number
  readonly height?: number
  readonly quality?: ImageQuality
  readonly resize?: 'cover' | 'contain' | 'fill'
  readonly format?: string
}

/**
 * Get optimized image URL using Supabase storage transformations
 * STRICT TYPING: All parameters and return values are strictly typed
 */
export function getOptimizedImageUrl(
  imageUrl: string | null | undefined, 
  options: ImageOptimizationOptions = {}
): string {
  try {
    if (!imageUrl || imageUrl === '/placeholder.png') {
      return imageUrl ?? '/placeholder.png'
    }

    // Check if this is a Supabase storage URL
    const isSupabaseUrl: boolean = imageUrl.includes('.supabase.co/storage/v1/')
    if (!isSupabaseUrl) {
      // For non-Supabase URLs, return as-is (external URLs, existing URLs)
      return imageUrl
    }

    const {
      width,
      height = width, // Default to square if only width provided
      quality = IMAGE_QUALITY.MEDIUM,
      resize = 'cover',
      format
    } = options

    // If no transformations requested, return original
    if (!width && !height && quality === IMAGE_QUALITY.MEDIUM && !format) {
      return imageUrl
    }

    // Build transform parameters
    const params: URLSearchParams = new URLSearchParams()

    if (width) params.append('width', width.toString())
    if (height) params.append('height', height.toString())
    if (quality !== IMAGE_QUALITY.MEDIUM) params.append('quality', quality.toString())
    if (resize !== 'cover') params.append('resize', resize)
    if (format) params.append('format', format)

    // Convert public URL to render URL for transformations
    const transformUrl: string = imageUrl.replace(
      '/storage/v1/object/public/',
      '/storage/v1/render/image/public/'
    )

    return `${transformUrl}?${params.toString()}`
  } catch (error) {
    console.warn('Image URL optimization failed, returning original:', error)
    return imageUrl ?? '/placeholder.png'
  }
}

/**
 * Get avatar-sized image optimized for circular display
 * Perfect for user lists, navigation, small circular displays
 * Generates a square image that's centered on faces
 */
export function getAvatarImage(imageUrl: string | null | undefined): string {
  try {
    return getOptimizedImageUrl(imageUrl, {
      width: IMAGE_SIZES.SMALL, // 150px - consolidated size
      height: IMAGE_SIZES.SMALL, // 150px - force square aspect ratio
      quality: IMAGE_QUALITY.MEDIUM,
      resize: 'cover' // This will crop to fill the square, centering faces
    })
  } catch (error) {
    console.warn('Avatar image optimization failed, falling back to original:', error)
    return imageUrl ?? '/placeholder.png'
  }
}

/**
 * Get card-sized image (150x150px, medium quality)
 * Perfect for user cards in grids, love cards, browse views
 */
export function getCardImage(imageUrl: string | null | undefined): string {
  return getOptimizedImageUrl(imageUrl, {
    width: IMAGE_SIZES.SMALL,
    height: IMAGE_SIZES.SMALL,
    quality: IMAGE_QUALITY.MEDIUM
  })
}

/**
 * Get profile card image optimized for portrait photos
 * Perfect for love cards and user browsing where faces are important
 * Generates a square image that's centered on the subject
 */
export function getProfileCardImage(imageUrl: string | null | undefined): string {
  return getOptimizedImageUrl(imageUrl, {
    width: IMAGE_SIZES.LARGE, // 300px - consolidated size
    height: IMAGE_SIZES.LARGE, // 300px - force square aspect ratio
    quality: IMAGE_QUALITY.MEDIUM,
    resize: 'cover' // This will crop to fill the square, centering the subject
  })
}

/**
 * Get profile-sized image (300x300px, high quality)
 * Perfect for profile headers, detailed views, photo modals
 */
export function getProfileImage(imageUrl: string | null | undefined): string {
  return getOptimizedImageUrl(imageUrl, {
    width: IMAGE_SIZES.LARGE,
    height: IMAGE_SIZES.LARGE,
    quality: IMAGE_QUALITY.HIGH
  })
}

/**
 * Get game-sized image (150x150px, medium quality)
 * Perfect for memory games, face guessing challenges
 */
export function getGameImage(imageUrl: string | null | undefined): string {
  try {
    return getOptimizedImageUrl(imageUrl, {
      width: IMAGE_SIZES.SMALL,
      height: IMAGE_SIZES.SMALL,
      quality: IMAGE_QUALITY.MEDIUM
    })
  } catch (error) {
    console.warn('Game image optimization failed, falling back to original:', error)
    return imageUrl ?? '/placeholder.png'
  }
}

/**
 * Get responsive image URL based on device pixel ratio
 * Uses consolidated sizes to avoid creating unique transformations
 */
export function getResponsiveImage(imageUrl: string | null | undefined, baseWidth: number): string {
  const pixelRatio: number = window.devicePixelRatio || 1
  
  // Use consolidated sizes to avoid unique transformations
  const useHighRes = pixelRatio > 1 || baseWidth > IMAGE_SIZES.SMALL
  const targetSize = useHighRes ? IMAGE_SIZES.LARGE : IMAGE_SIZES.SMALL

  return getOptimizedImageUrl(imageUrl, {
    width: targetSize,
    height: targetSize,
    quality: useHighRes ? IMAGE_QUALITY.HIGH : IMAGE_QUALITY.MEDIUM
  })
}

/**
 * Preload critical images for better performance
 * Use for above-the-fold content or next-page preloading
 */
export function preloadImage(imageUrl: string | null | undefined): void {
  if (!imageUrl || typeof window === 'undefined') return

  const link: HTMLLinkElement = document.createElement('link')
  link.rel = 'preload'
  link.as = 'image'
  link.href = imageUrl
  document.head.appendChild(link)
}

/**
 * Create a blur placeholder data URL for loading states
 * Generates a tiny blurred version of the image
 */
export function getBlurPlaceholder(imageUrl: string | null | undefined): string {
  if (!imageUrl || imageUrl === '/placeholder.png') {
    return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHZpZXdCb3g9IjAgMCAyMCAyMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjIwIiBoZWlnaHQ9IjIwIiBmaWxsPSIjRjNGNEY2Ii8+Cjwvc3ZnPgo='
  }

  return getOptimizedImageUrl(imageUrl, {
    width: 20,
    height: 20,
    quality: IMAGE_QUALITY.LOW
  })
}
